import siteCached from '@/services/utils/siteCached';
import type { IHandler } from '@/utils/chain-of-responsibility';
import { isNullOrZero } from '@/utils/null-utils';
import { PageType } from '../../../models/Page';
import ConfigurationService from '../../ConfigurationService';
import {
  EventType,
  type InteractionDetails
} from '../../UserInteractionService/IInteractionDetails';
import AttentiveDataHandler from './AttentiveDataHandler';
import AttentiveService from './AttentiveService';

/**
 * An {@link IHandler} for Attentive SDK analytics events.
 *
 * Handles product view, add-to-cart, and purchase events by transforming
 * internal interaction details into Attentive SDK event payloads.
 *
 * **Config-gated:** Only processes events when Attentive is enabled in configuration.
 */
export default class AttentiveUserInteractionHandler
  implements IHandler<InteractionDetails, Promise<void>>
{
  private readonly dataHandler: AttentiveDataHandler =
    new AttentiveDataHandler();

  /**
   * Whether Attentive integration is enabled.
   * Cached per site to avoid repeated config lookups.
   * @returns A boolean.
   */
  @siteCached
  private get isEnabled(): boolean {
    return ConfigurationService.getConfig('attentive').getSetting('enabled')
      .value;
  }

  /** @inheritdoc */
  public async handle(
    requestData: InteractionDetails,
    next: (requestData: InteractionDetails) => Promise<void>
  ): Promise<void> {
    if (this.isEnabled) {
      switch (requestData.action) {
        case EventType.PageView: {
          // Only fire productView if this is a PDP page with products
          if (
            !isNullOrZero(requestData.products?.length) &&
            requestData.page.pageType === PageType.Product
          ) {
            const payload = await this.dataHandler.getProductViewData(
              requestData.products![0]
            );
            AttentiveService.sendProductView(payload);
          }

          // Always call next to allow other handlers to process PageView events
          return next(requestData);
        }

        case EventType.ProductAdd: {
          const payload = await this.dataHandler.getAddToCartData(
            requestData.product
          );
          AttentiveService.sendAddToCart(payload);
          return next(requestData);
        }

        case EventType.OrderSuccess: {
          const payload = await this.dataHandler.getPurchaseData(
            requestData.orderData,
            requestData.cartData
          );
          AttentiveService.sendPurchase(payload);
          return next(requestData);
        }

        default:
          return next(requestData);
      }
    }

    return next(requestData);
  }
}
